import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';

const handleRenewalClick3 = (url: string) => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = url;
  };

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  const [renewalSuccess, setRenewalSuccess] = useState(false);
  const [renewalMessage, setRenewalMessage] = useState('');
  const [processingRenewal, setProcessingRenewal] = useState(false);
  const [invalidParamsMessage, setInvalidParamsMessage] = useState('');
  const { initialize } = useAuthStore();

  const renewDays = searchParams.get('renew_days');
  const sessionId = searchParams.get('session_id');
  const paymentIntent = searchParams.get('payment_intent');

  const isFromStripe = () => {
    if (typeof window === 'undefined') return false;
    
    const currentUrl = window.location.href.toLowerCase();
    const referrer = document.referrer.toLowerCase();
    
    const stripePatterns = [
      /\/pay\/cs_/,
      /payment_intent/,
      /stripe\.com/,
      /redirect_from=stripe/,
      /_stripe_session_id/,
      /^https:\/\/buy\.stripe\.com/,
      /^https:\/\/checkout\.stripe\.com/,
      /cs_test_/,
      /cs_live_/,
      /buy\.stripe\.com\/c\/pay/
    ];

    const isStripeUrl = stripePatterns.some(pattern => pattern.test(currentUrl));
    const isFromStripeReferrer = stripePatterns.some(pattern => pattern.test(referrer));
    const isAiauraWithRenewDays = currentUrl.includes('aiaura.com.br') && 
                                  currentUrl.includes('renew_days=') &&
                                  (isStripeUrl || isFromStripeReferrer || 
                                   document.referrer.includes('stripe.com'));
    
    return isStripeUrl || isFromStripeReferrer || isAiauraWithRenewDays;
  };

  useEffect(() => {
    const handleOnline = () => setNetworkStatus(true);
    const handleOffline = () => setNetworkStatus(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const savedCredentials = sessionStorage.getItem('tempLoginCredentials');
    if (savedCredentials) {
      const { email: savedEmail, password: savedPassword } = JSON.parse(savedCredentials);
      setEmail(savedEmail);
      setPassword(savedPassword);
      sessionStorage.removeItem('tempLoginCredentials');
      handleSubmit(new Event('submit') as any);
    }
  }, []);

  useEffect(() => {
    if (renewDays) {
      const fromStripe = isFromStripe();
      if (!fromStripe) {
        setInvalidParamsMessage("⚠️ Link de renovação inválido. Acesse pelo painel ou entre em contato com o suporte no fim da página.");
      } else {
        setInvalidParamsMessage('');
      }
    }
  }, [renewDays]);

  useEffect(() => {
    if (renewDays && isFromStripe()) {
      const timer = setTimeout(() => {
        localStorage.removeItem('tempLoginCredentials');
        sessionStorage.removeItem('tempLoginCredentials');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [renewDays]);

  useEffect(() => {
    if (renewalSuccess) {
      const timer = setTimeout(() => {
        setRenewalSuccess(false);
        setRenewalMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [renewalSuccess]);

  const formatDateForDb = (date: Date): string => {
    return date.toISOString().replace('T', ' ').replace('Z', '+00');
  };

  const getUserIdByEmail = async (email: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .single();
      
      if (error) throw error;
      
      return data?.id || null;
    } catch (err) {
      throw err;
    }
  };

  const updateSubscription = async (userId: string) => {
    if (!renewDays) return false;
    
    try {
      const days = parseInt(renewDays);
      if (![7, 15, 30, 365].includes(days)) {
        throw new Error(`Período de renovação inválido: ${days}`);
      }

      const { data: subscription, error: fetchError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (fetchError) throw fetchError;

      const currentDate = new Date();
      const newExpirationDate = new Date(currentDate);
      const expirationDays = days === 15 ? 30 : days;
      newExpirationDate.setDate(currentDate.getDate() + expirationDays);

      const subscriptionData = {
        plan: days === 15 ? 'essential' : 
              days === 7 ? 'test' : 
              days === 30 ? 'pro' : 'unlimited',
        status: 'active',
        next_billing_date: formatDateForDb(newExpirationDate),
        updated_at: formatDateForDb(currentDate),
        last_renewal_date: formatDateForDb(currentDate),
        request_count: days === 15 ? 30 : (days === 7 ? 7 : (subscription?.request_count || 0))
      };

      if (!subscription) {
        const { error: insertError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            created_at: formatDateForDb(currentDate),
            ...subscriptionData
          });

        if (insertError) throw insertError;
      } else {
        const { error: updateError } = await supabase
          .from('subscriptions')
          .update(subscriptionData)
          .eq('id', subscription.id);

        if (updateError) throw updateError;
      }

      setRenewalMessage(`Assinatura ${subscription ? 'atualizada' : 'criada'} com sucesso! Válida por ${expirationDays} dias`);
      setRenewalSuccess(true);
      return true;
    } catch (err) {
      setError(`Erro na renovação: ${err instanceof Error ? err.message : 'Erro desconhecido'}`);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!networkStatus) {
      setError('Você está offline. Conecte-se à internet para continuar.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      if (!supabase || !supabase.auth) {
        throw new Error('Erro: Cliente Supabase não está devidamente inicializado');
      }

      const credentials = {
        email: email.trim(),
        password: password.trim()
      };

      sessionStorage.setItem('tempLoginCredentials', JSON.stringify(credentials));

      const authPromise = supabase.auth.signInWithPassword(credentials);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: A autenticação demorou muito')), 5000)
      );

      const { data: { user, session }, error: authError } = 
        await Promise.race([authPromise, timeoutPromise]);

      if (authError) throw authError;
      if (!user || !session) throw new Error('Autenticação falhou: usuário ou sessão não retornados');
      
      if (renewDays && isFromStripe()) {
        setProcessingRenewal(true);
        try {
          const success = await updateSubscription(user.id);
          if (!success) throw new Error('Falha ao atualizar assinatura');
          await new Promise(resolve => setTimeout(resolve, 500));
        } finally {
          setProcessingRenewal(false);
        }
      }

      localStorage.setItem('rememberMe', rememberMe.toString());
      await initialize();
      sessionStorage.removeItem('tempLoginCredentials');
      navigate('/dashboard/leads');

    } catch (err) {
      let errorMessage = 'Erro durante o login';
      if (err instanceof Error) {
        if (err.message.includes('Invalid login credentials')) {
          errorMessage = 'E-mail ou senha incorretos';
        } else if (err.message.includes('Email not confirmed')) {
          errorMessage = 'Confirme seu e-mail antes de fazer login';
        } else if (err.message.includes('Timeout')) {
          localStorage.clear();
          sessionStorage.clear();
          window.location.reload();
          return;
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
            Entre na sua conta
          </h2>          
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md border border-red-200 dark:border-red-800">
            <p className="text-red-600 dark:text-red-300 text-sm font-medium whitespace-pre-line">
              {error}
            </p>
          </div>
        )}

        {invalidParamsMessage && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md border border-yellow-200 dark:border-yellow-800">
            <p className="text-yellow-700 dark:text-yellow-300 text-sm font-medium">
              {invalidParamsMessage}
            </p>
          </div>
        )}

        {renewalSuccess && (
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md border border-green-200 dark:border-green-800 animate-fade-in">
            <p className="text-green-600 dark:text-green-300 text-sm font-medium">
              {renewalMessage}
            </p>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Endereço de e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || processingRenewal}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Senha
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || processingRenewal}
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/*<input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading || processingRenewal}
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Lembrar de mim
              </label>*/}
            </div>
            <div className="text-sm">
              <Link onClick={() => handleRenewalClick3('https://wa.me/5534996687855?text=Olá! Esqueci minha senha')} className="font-medium text-indigo-600 hover:text-indigo-500">
                Esqueceu sua senha?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !networkStatus || processingRenewal}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors ${
                loading || !networkStatus || processingRenewal ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {processingRenewal ? (
                'Processando renovação...'
              ) : loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Autenticando...
                </>
              ) : !networkStatus ? (
                'Sem conexão com a internet'
              ) : (
                'Entrar na minha conta'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;